/**
 * Resolves a user object from AuthContext to a dashboard layout key.
 *
 * Order matters: admin/super_admin mirror CEO regardless of position, and
 * position_name takes priority over the legacy `role` field. Returns
 * 'default' for anyone who doesn't match — they get a minimal layout
 * (just MyWork + welcome banner).
 *
 * Keep this as a pure function so it's trivially unit-testable and can be
 * used both inside React (via AuthContext memo) and in tests.
 *
 * @param {object|null} user  User record from AuthContext (as returned by /auth/me)
 * @returns {'ceo'|'apollo_gm'|'papas_lot_gm'|'truck_maintenance'|'sales_admin'|'sign_technician'|'dispatcher'|'sales'|'driver'|'default'}
 */
export default function resolveDashboardRole(user) {
  if (!user) return 'default';

  const tier = user.permission_tier;
  const position = user.position_name;

  // Tier override: admins and super admins see the CEO dashboard.
  if (tier === 'super_admin' || tier === 'admin') return 'ceo';

  // Position-based resolution (exact match, case-sensitive — matches
  // company_positions.name values seeded in database.js).
  switch (position) {
    case 'Apollo GM':           return 'apollo_gm';
    case "Papa's Lot GM":       return 'papas_lot_gm';
    case 'Truck Maintenance':
    case 'Maintenance Technician':
      return 'truck_maintenance';
    case 'Sales Admin':         return 'sales_admin';
    case 'Sign Technician':     return 'sign_technician';
    case 'Dispatcher':          return 'dispatcher';
    case 'Sales':
    case 'Sales Rep':
      return 'sales';
    case 'Driver':
    case 'Day Driver':
    case 'Night Driver':
      return 'driver';
    default:
      return 'default';
  }
}
